import React, { useMemo, useState } from "react";
import {
    Button,
    Header,
    Icon,
    Label,
    Message,
    Popup,
    Progress,
    Segment,
    Table,
} from "semantic-ui-react";
import {
    DataQualityIssue,
    OrderDetailScope,
    OrderDashboardResponse,
    ProductMetric,
    ReportFilters,
} from "../../../types/models/Reporting";
import DataQualityModal from "../modals/DataQualityModal";
import DashboardOrdersModal, {
    DashboardOrdersDrilldown,
} from "../modals/DashboardOrdersModal";
import ProductOrdersModal from "../modals/ProductOrdersModal";
import {
    formatCurrency,
    formatMonth,
    formatNumber,
    formatPercent,
    orderStatusColor,
    orderStatusLabel,
    riskColor,
    riskLabel,
} from "../reportingFormatters";

interface Props {
    data?: OrderDashboardResponse;
    filters: ReportFilters;
    loading: boolean;
    error?: string;
    onOpenPriority: () => void;
}

interface QualityCard {
    issue: DataQualityIssue;
    label: string;
    value: number;
    icon: any;
}

interface DashboardKpi {
    label: string;
    value: string;
    detail: string;
    icon: any;
    tone: string;
    scope: OrderDetailScope;
    description: string;
    summary: Array<{ label: string; value: string }>;
}

const AGING_SCOPES: Record<string, OrderDetailScope> = {
    "0_7": "AGING_0_7",
    "8_14": "AGING_8_14",
    "15_30": "AGING_15_30",
    "31_PLUS": "AGING_31_PLUS",
};

const monthDateRange = (month: string) => {
    const [year, monthNumber] = month.slice(0, 7).split("-").map(Number);
    const from = `${year}-${String(monthNumber).padStart(2, "0")}-01`;
    const to = new Date(Date.UTC(year, monthNumber, 0)).toISOString().slice(0, 10);
    return { from, to };
};

const activateRow = (event: React.KeyboardEvent, action: () => void) => {
    if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        action();
    }
};

const paymentMethodDescription = (code: string) => {
    switch (code) {
        case "PAYMENT_METHOD_PENDING":
            return "El pedido todavía no llega a Agendado; el método de pago se definirá al agendar la instalación.";
        case "MISSING_PAYMENT_METHOD":
            return "El pedido ya está Agendado, Inconcluso o Terminado y debería tener un método de pago. Se considera una incidencia de calidad.";
        case "CANCELED_NO_PAYMENT_METHOD":
            return "El pedido fue cancelado sin que se registrara un método de pago.";
        default:
            return "Incluye los pedidos del periodo que tienen registrado este método de pago.";
    }
};

const paymentMethodHelp = (
    <div className="dashboard-payment-help">
        <p><strong>Transferencia, Efectivo o Tarjeta:</strong> el pedido ya tiene un método de pago registrado.</p>
        <p><strong>Método pendiente:</strong> todavía no llega a Agendado; el método se definirá más adelante.</p>
        <p><strong>Método faltante:</strong> ya está Agendado, Inconcluso o Terminado y debería tener método. Se reporta como incidencia de calidad.</p>
        <p><strong>Cancelado · método no definido:</strong> fue cancelado antes de registrar un método de pago.</p>
    </div>
);

const ExecutiveDashboard = ({ data, filters, loading, error, onOpenPriority }: Props) => {
    const [qualityIssue, setQualityIssue] = useState<DataQualityIssue>();
    const [selectedProduct, setSelectedProduct] = useState<ProductMetric>();
    const [ordersDrilldown, setOrdersDrilldown] = useState<DashboardOrdersDrilldown>();

    const qualityCards = useMemo<QualityCard[]>(() => {
        if (!data) return [];
        const cards: QualityCard[] = [
            { issue: "ZERO_VALUE", label: "Pedidos con valor cero", value: data.dataQuality.zeroValueOrders, icon: "dollar" },
            { issue: "MISSING_CLIENT_OR_ADDRESS", label: "Sin cliente o dirección", value: data.dataQuality.missingClientOrAddress, icon: "address card outline" },
            { issue: "MISSING_PAYMENT_METHOD", label: "Método faltante desde Agendado", value: data.dataQuality.missingPaymentMethod, icon: "credit card outline" },
            { issue: "SCHEDULED_WITHOUT_INSTALLER", label: "Agendados sin instalador", value: data.dataQuality.scheduledWithoutInstaller, icon: "user outline" },
            { issue: "SCHEDULED_WITHOUT_COORDINATES", label: "Agendados sin coordenadas", value: data.dataQuality.scheduledWithoutCoordinates, icon: "map marker alternate" },
            { issue: "ZERO_AMOUNT_PAYMENT", label: "Pedidos con pagos en cero", value: data.dataQuality.zeroAmountPayments, icon: "minus circle" },
            { issue: "STATUS_TRANSITION_WITHOUT_USER", label: "Pedidos con transiciones sin usuario", value: data.dataQuality.ordersWithUnattributedTransitions, icon: "user times" },
            { issue: "STATUS_HISTORY_DISCONTINUITY", label: "Historiales discontinuos", value: data.dataQuality.statusHistoryDiscontinuities, icon: "history" },
            { issue: "GUARANTEE_WITHOUT_ADVISOR", label: "Garantías sin responsable", value: data.dataQuality.guaranteesWithoutAdvisor, icon: "shield alternate" },
            { issue: "PROSPECT_SALE_STATUS_MISMATCH", label: "Ventas con estado desactualizado", value: data.dataQuality.prospectSaleStatusMismatch, icon: "exchange" },
        ];
        return cards.filter((item) => item.value > 0);
    }, [data]);

    if (error) {
        return <Message negative icon="warning circle" header="No fue posible cargar el dashboard" content={error} />;
    }
    if (!data && !loading) {
        return <Message info icon="info circle" content="Aplica los filtros para consultar la vista gerencial." />;
    }
    if (!data) return null;

    const operationalStatuses = data.byStatus.filter((item) => item.code !== "COMPLETED");
    const maxStatusOrders = Math.max(...operationalStatuses.map((item) => item.orderCount), 1);
    const maxAgingOrders = Math.max(...data.activeOrderAging.map((item) => item.orderCount), 1);

    const kpis: DashboardKpi[] = [
        {
            label: "Pedidos activos", value: formatNumber(data.summary.activeOrders), detail: `${formatNumber(data.summary.totalOrders)} históricos`, icon: "box", tone: "blue", scope: "ACTIVE",
            description: "Pedidos del periodo que continúan dentro del flujo operativo activo.",
            summary: [
                { label: "Pedidos activos", value: formatNumber(data.summary.activeOrders) },
                { label: "Valor en operación", value: formatCurrency(data.summary.activePipelineAmount) },
                { label: "Saldo activo", value: formatCurrency(data.summary.activeOutstandingBalance) },
            ],
        },
        {
            label: "Valor en operación", value: formatCurrency(data.summary.activePipelineAmount), detail: "Pipeline activo", icon: "chart line", tone: "teal", scope: "ACTIVE",
            description: "Pedidos activos que forman el valor actualmente en operación.",
            summary: [
                { label: "Valor en operación", value: formatCurrency(data.summary.activePipelineAmount) },
                { label: "Pedidos activos", value: formatNumber(data.summary.activeOrders) },
                { label: "Saldo activo", value: formatCurrency(data.summary.activeOutstandingBalance) },
            ],
        },
        {
            label: "Saldo activo pendiente", value: formatCurrency(data.summary.activeOutstandingBalance), detail: "Exposición por cobrar", icon: "money bill alternate outline", tone: "orange", scope: "ACTIVE_OUTSTANDING",
            description: "Pedidos activos que aún conservan un saldo mayor a cero.",
            summary: [
                { label: "Saldo activo", value: formatCurrency(data.summary.activeOutstandingBalance) },
                { label: "Pedidos activos", value: formatNumber(data.summary.activeOrders) },
                { label: "Cobrado", value: formatCurrency(data.summary.paidAmount) },
            ],
        },
        {
            label: "Cobranza acumulada", value: formatPercent(data.summary.collectionRatePercent), detail: `${formatCurrency(data.summary.paidAmount)} cobrado`, icon: "percent", tone: "green", scope: "ALL",
            description: "Todos los pedidos del periodo que forman el total vendido, cobrado y pendiente.",
            summary: [
                { label: "Cobranza", value: formatPercent(data.summary.collectionRatePercent) },
                { label: "Cobrado", value: formatCurrency(data.summary.paidAmount) },
                { label: "Valor total", value: formatCurrency(data.summary.totalAmount) },
                { label: "Saldo", value: formatCurrency(data.summary.outstandingBalance) },
            ],
        },
        {
            label: "Proyectos terminados", value: formatNumber(data.summary.completedOrders), detail: `${formatNumber(data.summary.canceledOrders)} cancelados`, icon: "check circle outline", tone: "blue", scope: "COMPLETED",
            description: "Pedidos del periodo cuyo estado actual es Terminado.",
            summary: [
                { label: "Terminados", value: formatNumber(data.summary.completedOrders) },
                { label: "Cancelados", value: formatNumber(data.summary.canceledOrders) },
            ],
        },
        {
            label: "Ticket promedio", value: formatCurrency(data.summary.averageTicket), detail: "Valor medio por pedido", icon: "ticket alternate", tone: "violet", scope: "ALL",
            description: "Todos los pedidos utilizados para calcular el valor promedio del periodo.",
            summary: [
                { label: "Ticket promedio", value: formatCurrency(data.summary.averageTicket) },
                { label: "Pedidos", value: formatNumber(data.summary.totalOrders) },
                { label: "Valor total", value: formatCurrency(data.summary.totalAmount) },
            ],
        },
        {
            label: "Garantías abiertas", value: formatNumber(data.summary.openGuarantees), detail: "Seguimiento posventa", icon: "shield alternate", tone: "red", scope: "OPEN_GUARANTEE",
            description: "Pedidos del periodo que tienen una o más garantías todavía abiertas.",
            summary: [
                { label: "Garantías abiertas", value: formatNumber(data.summary.openGuarantees) },
            ],
        },
        {
            label: "Instalaciones agendadas", value: formatNumber(data.summary.scheduledInstallations), detail: `${formatNumber(data.summary.scheduledInstallationsWithPaymentProof)} con comprobante`, icon: "calendar check outline", tone: "yellow", scope: "SCHEDULED_INSTALLATION",
            description: "Pedidos del periodo en etapa Agendado que tienen una fecha de instalación asignada.",
            summary: [
                { label: "Con instalación", value: formatNumber(data.summary.scheduledInstallations) },
                { label: "Con comprobante", value: formatNumber(data.summary.scheduledInstallationsWithPaymentProof) },
            ],
        },
    ];

    return (
        <div className="dashboard-section">
            <div className="dashboard-section-heading">
                <div>
                    <span className="dashboard-eyebrow">Resumen ejecutivo</span>
                    <h2>Estado del negocio al corte</h2>
                    <p>Corte {data.cutoff} · {data.timeZone}</p>
                </div>
            </div>

            <div className="dashboard-kpi-grid">
                {kpis.map((kpi) => (
                    <article
                        className={`dashboard-kpi dashboard-kpi--${kpi.tone} dashboard-kpi--clickable`}
                        key={kpi.label}
                        role="button"
                        tabIndex={0}
                        onClick={() => setOrdersDrilldown({
                            key: `kpi-${kpi.label}`,
                            title: kpi.label,
                            description: kpi.description,
                            icon: kpi.icon,
                            filters: { ...filters, attentionOnly: false, scope: kpi.scope },
                            summary: kpi.summary,
                        })}
                        onKeyDown={(event: React.KeyboardEvent) => activateRow(event, () => setOrdersDrilldown({
                            key: `kpi-${kpi.label}`,
                            title: kpi.label,
                            description: kpi.description,
                            icon: kpi.icon,
                            filters: { ...filters, attentionOnly: false, scope: kpi.scope },
                            summary: kpi.summary,
                        }))}
                    >
                        <div className="dashboard-kpi__icon"><Icon name={kpi.icon as any} /></div>
                        <div>
                            <span>{kpi.label}</span>
                            <strong>{kpi.value}</strong>
                            <small>{kpi.detail}</small>
                        </div>
                    </article>
                ))}
            </div>

            <Segment className="dashboard-attention" loading={loading}>
                <div className="dashboard-section-heading dashboard-section-heading--compact">
                    <div>
                        <span className="dashboard-eyebrow dashboard-eyebrow--danger">Atención gerencial</span>
                        <h3>Riesgo y bloqueos operativos</h3>
                    </div>
                    <Button basic color="red" size="small" onClick={onOpenPriority}>
                        Revisar pedidos <Icon name="arrow right" />
                    </Button>
                </div>
                <p className="dashboard-muted">Esta sección siempre muestra el backlog vigente al corte; el rango de creación no oculta pendientes antiguos.</p>
                <div className="dashboard-attention-grid">
                    <div><strong>{formatNumber(data.attention.criticalOrders)}</strong><span>Casos críticos</span></div>
                    <div><strong>{formatNumber(data.attention.requiresAttention)}</strong><span>Requieren atención</span></div>
                    <div><strong>{formatNumber(data.attention.stalledOrders)}</strong><span>Etapas detenidas</span></div>
                    <div><strong>{formatNumber(data.attention.overdueInstallations)}</strong><span>Instalaciones vencidas</span></div>
                    <div><strong>{formatCurrency(data.attention.amountAtRisk)}</strong><span>Exposición por atender</span></div>
                </div>
            </Segment>

            <div className="dashboard-two-columns">
                <Segment>
                    <Header as="h3"><Icon name="hourglass half" /> Antigüedad de pedidos activos</Header>
                    <div className="dashboard-bars">
                        {data.activeOrderAging.map((item) => (
                            <button
                                type="button"
                                className="dashboard-bar dashboard-summary-row-button"
                                key={item.bucket}
                                onClick={() => setOrdersDrilldown({
                                    key: `aging-${item.bucket}`,
                                    title: `Antigüedad activa · ${item.label}`,
                                    description: "Pedidos activos ubicados en este rango según los días transcurridos en su etapa actual. El backlog no se limita al mes de creación.",
                                    icon: "hourglass half",
                                    filters: {
                                        ...filters,
                                        from: undefined,
                                        to: undefined,
                                        attentionOnly: false,
                                        scope: AGING_SCOPES[item.bucket] || "ACTIVE",
                                    },
                                    summary: [
                                        { label: "Pedidos", value: formatNumber(item.orderCount) },
                                        { label: "Valor en operación", value: formatCurrency(item.pipelineAmount) },
                                        { label: "Saldo", value: formatCurrency(item.outstandingBalance) },
                                    ],
                                })}
                            >
                                <div className="dashboard-bar__label">
                                    <strong>{item.label}</strong>
                                    <span>{formatNumber(item.orderCount)} · {formatCurrency(item.outstandingBalance)}</span>
                                </div>
                                <div className="dashboard-bar__track"><span style={{ width: `${Math.max((item.orderCount / maxAgingOrders) * 100, 3)}%` }} /></div>
                            </button>
                        ))}
                    </div>
                </Segment>

                <Segment>
                    <Header as="h3"><Icon name="tasks" /> Etapas operativas</Header>
                    <div className="dashboard-status-list">
                        {operationalStatuses.map((item) => (
                            <button
                                type="button"
                                className="dashboard-status-row dashboard-summary-row-button"
                                key={item.code}
                                onClick={() => setOrdersDrilldown({
                                    key: `status-${item.code}`,
                                    title: item.code === "UNKNOWN" ? `Etapa operativa · ${item.label}` : "Etapa operativa",
                                    status: item.code === "UNKNOWN" ? undefined : item.code,
                                    description: "Pedidos del periodo cuyo estado actual corresponde a esta etapa operativa.",
                                    icon: "tasks",
                                    filters: {
                                        ...filters,
                                        status: item.code === "UNKNOWN" ? undefined : item.code,
                                        attentionOnly: false,
                                        scope: item.code === "UNKNOWN" ? "STATUS_UNKNOWN" : "ALL",
                                    },
                                    summary: [
                                        { label: "Pedidos", value: formatNumber(item.orderCount) },
                                        { label: "Valor", value: formatCurrency(item.totalAmount) },
                                        { label: "Saldo", value: formatCurrency(item.outstandingBalance) },
                                        { label: "Promedio en etapa", value: `${Number(item.averageDaysInStatus).toFixed(1)} días` },
                                        { label: "Máximo en etapa", value: `${formatNumber(item.maximumDaysInStatus)} días` },
                                        { label: "Detenidos", value: formatNumber(item.stalledOrders) },
                                    ],
                                })}
                            >
                                <div className="dashboard-bar__label">
                                    <strong>
                                        <Label color={orderStatusColor(item.code)}>
                                            {orderStatusLabel(item.code, item.label)}
                                        </Label>
                                    </strong>
                                    <span>{formatNumber(item.orderCount)} · {Number(item.averageDaysInStatus).toFixed(1)} días · {formatCurrency(item.outstandingBalance)}</span>
                                </div>
                                <Progress
                                    percent={Math.max((item.orderCount / maxStatusOrders) * 100, 2)}
                                    size="tiny"
                                    color={orderStatusColor(item.code)}
                                />
                            </button>
                        ))}
                    </div>
                </Segment>
            </div>

            {data.priorityOrders.length > 0 && (
            <Segment>
                <div className="dashboard-section-heading dashboard-section-heading--compact">
                    <div>
                        <span className="dashboard-eyebrow">Decisiones prioritarias</span>
                        <h3>Pedidos ordenados por riesgo y exposición</h3>
                    </div>
                    <Button basic size="small" onClick={onOpenPriority}>Ver reporte completo</Button>
                </div>
                <div className="dashboard-table-scroll">
                    <Table compact selectable className="dashboard-table">
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Prioridad</Table.HeaderCell>
                                <Table.HeaderCell>Factura</Table.HeaderCell>
                                <Table.HeaderCell>Motivos</Table.HeaderCell>
                                <Table.HeaderCell>Estado / antigüedad</Table.HeaderCell>
                                <Table.HeaderCell textAlign="right">Saldo</Table.HeaderCell>
                                <Table.HeaderCell>Responsable</Table.HeaderCell>
                                <Table.HeaderCell>Siguiente acción</Table.HeaderCell>
                                <Table.HeaderCell />
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {data.priorityOrders.map((order) => (
                                <Table.Row
                                    key={order.orderId}
                                    className="dashboard-clickable-row"
                                    tabIndex={0}
                                    onClick={() => setOrdersDrilldown({
                                        key: `priority-${order.orderId}`,
                                        title: `Pedido prioritario #${order.invoice || "sin factura"}`,
                                        description: "Muestra el pedido que origina esta alerta, sin limitarlo por el mes de creación seleccionado.",
                                        icon: "warning sign",
                                        filters: {
                                            ...filters,
                                            from: undefined,
                                            to: undefined,
                                            attentionOnly: false,
                                            search: order.invoice ? String(order.invoice) : order.orderId,
                                        },
                                        summary: [
                                            { label: "Prioridad", value: `${riskLabel(order.riskLevel)} · ${order.priorityScore}` },
                                            { label: "Estado", value: order.statusLabel, status: order.status },
                                            { label: "Días en etapa", value: formatNumber(order.daysInCurrentStatus) },
                                            { label: "Saldo", value: formatCurrency(order.outstandingBalance) },
                                        ],
                                    })}
                                    onKeyDown={(event: React.KeyboardEvent) => activateRow(event, () => setOrdersDrilldown({
                                        key: `priority-${order.orderId}`,
                                        title: `Pedido prioritario #${order.invoice || "sin factura"}`,
                                        description: "Muestra el pedido que origina esta alerta, sin limitarlo por el mes de creación seleccionado.",
                                        icon: "warning sign",
                                        filters: { ...filters, from: undefined, to: undefined, attentionOnly: false, search: order.invoice ? String(order.invoice) : order.orderId },
                                        summary: [
                                            { label: "Prioridad", value: `${riskLabel(order.riskLevel)} · ${order.priorityScore}` },
                                            { label: "Estado", value: order.statusLabel, status: order.status },
                                            { label: "Días en etapa", value: formatNumber(order.daysInCurrentStatus) },
                                            { label: "Saldo", value: formatCurrency(order.outstandingBalance) },
                                        ],
                                    }))}
                                >
                                    <Table.Cell><Label color={riskColor(order.riskLevel)}>{riskLabel(order.riskLevel)} · {order.priorityScore}</Label></Table.Cell>
                                    <Table.Cell><strong>#{order.invoice || "—"}</strong></Table.Cell>
                                    <Table.Cell>
                                        <div className="dashboard-reasons">
                                            {order.reasons.map((reason) => <span key={reason}>{reason}</span>)}
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Label color={orderStatusColor(order.status)}>
                                            {orderStatusLabel(order.status, order.statusLabel)}
                                        </Label>
                                        <small>{order.daysInCurrentStatus} días</small>
                                    </Table.Cell>
                                    <Table.Cell textAlign="right"><strong>{formatCurrency(order.outstandingBalance)}</strong></Table.Cell>
                                    <Table.Cell>{order.advisorName || "Sin asignar"}</Table.Cell>
                                    <Table.Cell>{order.nextAction}</Table.Cell>
                                    <Table.Cell collapsing><Icon name="chevron right" color="grey" /></Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </div>
            </Segment>
            )}

            <div className="dashboard-full-width-stack">
                <Segment>
                    <Header as="h3"><Icon name="shopping bag" /> Saldo pendiente en pedidos activos</Header>
                    <p className="dashboard-muted">Excluye borradores, validación y cancelados. No incluye {formatCurrency(data.summary.productScopeExcludedBalance)} de saldo en borrador/validación.</p>
                    <div className="dashboard-table-scroll">
                        <Table compact basic="very" selectable className="dashboard-table dashboard-product-balance-table dashboard-clickable-table">
                            <Table.Header><Table.Row>
                                <Table.HeaderCell>Producto</Table.HeaderCell>
                                <Table.HeaderCell textAlign="right">Activos</Table.HeaderCell>
                                <Table.HeaderCell textAlign="right">Inconclusos</Table.HeaderCell>
                                <Table.HeaderCell textAlign="right">Terminados</Table.HeaderCell>
                                <Table.HeaderCell textAlign="right">Abonado</Table.HeaderCell>
                                <Table.HeaderCell textAlign="right">Saldo activo</Table.HeaderCell>
                                <Table.HeaderCell />
                            </Table.Row></Table.Header>
                            <Table.Body>{data.topProducts.map((item) => (
                                <Table.Row
                                    key={item.productId}
                                    className="dashboard-clickable-row"
                                    tabIndex={0}
                                    onClick={() => setSelectedProduct(item)}
                                    onKeyDown={(event: React.KeyboardEvent) => activateRow(event, () => setSelectedProduct(item))}
                                >
                                    <Table.Cell><strong>{item.name}</strong><small>{item.sku || "Sin SKU"}</small></Table.Cell>
                                    <Table.Cell textAlign="right"><strong>{formatCurrency(item.activeTotalAmount)}</strong><small>{formatNumber(item.activeOrders)} pedidos</small></Table.Cell>
                                    <Table.Cell textAlign="right"><strong>{formatCurrency(item.incompleteTotalAmount)}</strong><small>{formatNumber(item.incompleteOrders)} pedidos</small></Table.Cell>
                                    <Table.Cell textAlign="right"><strong>{formatCurrency(item.completedTotalAmount)}</strong><small>{formatNumber(item.completedOrders)} pedidos</small></Table.Cell>
                                    <Table.Cell textAlign="right">{formatCurrency(item.paidAmount)}</Table.Cell>
                                    <Table.Cell textAlign="right"><strong>{formatCurrency(item.activeOutstandingBalance)}</strong></Table.Cell>
                                    <Table.Cell collapsing><Icon name="chevron right" color="grey" /></Table.Cell>
                                </Table.Row>
                            ))}</Table.Body>
                        </Table>
                    </div>
                </Segment>
                <Segment>
                    <Header as="h3"><Icon name="chart line" /> Tendencia mensual</Header>
                    <div className="dashboard-table-scroll">
                        <Table compact basic="very" selectable className="dashboard-table dashboard-clickable-table">
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Mes</Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">Pedidos</Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">Terminados</Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">Valor de pedidos</Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">Cobranza</Table.HeaderCell>
                                    <Table.HeaderCell />
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {data.monthlyTrend.map((item) => (
                                    <Table.Row
                                        key={item.month}
                                        className="dashboard-clickable-row"
                                        tabIndex={0}
                                        onClick={() => {
                                            const range = monthDateRange(item.month);
                                            setOrdersDrilldown({
                                                key: `month-${item.month}`,
                                                title: `Tendencia mensual · ${formatMonth(item.month)}`,
                                                description: "Incluye los pedidos creados durante este mes y respeta los demás filtros del dashboard.",
                                                icon: "calendar alternate outline",
                                                filters: { ...filters, ...range, attentionOnly: false },
                                                summary: [
                                                    { label: "Pedidos", value: formatNumber(item.orderCount) },
                                                    { label: "Terminados", value: formatNumber(item.completedOrders) },
                                                    { label: "Valor", value: formatCurrency(item.salesAmount) },
                                                    { label: "Cobrado", value: formatCurrency(item.collectedToCutoff) },
                                                    { label: "Cobranza", value: formatPercent(item.collectionRatePercent) },
                                                ],
                                            });
                                        }}
                                        onKeyDown={(event: React.KeyboardEvent) => activateRow(event, () => {
                                            const range = monthDateRange(item.month);
                                            setOrdersDrilldown({
                                                key: `month-${item.month}`,
                                                title: `Tendencia mensual · ${formatMonth(item.month)}`,
                                                description: "Incluye los pedidos creados durante este mes y respeta los demás filtros del dashboard.",
                                                icon: "calendar alternate outline",
                                                filters: { ...filters, ...range, attentionOnly: false },
                                                summary: [
                                                    { label: "Pedidos", value: formatNumber(item.orderCount) },
                                                    { label: "Terminados", value: formatNumber(item.completedOrders) },
                                                    { label: "Valor", value: formatCurrency(item.salesAmount) },
                                                    { label: "Cobrado", value: formatCurrency(item.collectedToCutoff) },
                                                    { label: "Cobranza", value: formatPercent(item.collectionRatePercent) },
                                                ],
                                            });
                                        })}
                                    >
                                        <Table.Cell><strong>{formatMonth(item.month)}</strong></Table.Cell>
                                        <Table.Cell textAlign="right">{formatNumber(item.orderCount)}</Table.Cell>
                                        <Table.Cell textAlign="right">{formatNumber(item.completedOrders)}</Table.Cell>
                                        <Table.Cell textAlign="right">{formatCurrency(item.salesAmount)}</Table.Cell>
                                        <Table.Cell textAlign="right"><Label basic color={item.collectionRatePercent >= 90 ? "green" : "orange"}>{formatPercent(item.collectionRatePercent)}</Label></Table.Cell>
                                        <Table.Cell collapsing><Icon name="chevron right" color="grey" /></Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </div>
                </Segment>

                {qualityCards.length > 0 && (
                    <Segment>
                        <Header as="h3"><Icon name="database" /> Calidad de información</Header>
                        <p className="dashboard-muted">Selecciona una incidencia para revisar los registros que la originan.</p>
                        <div className="dashboard-quality-grid">
                            {qualityCards.map((item) => (
                                <button type="button" key={item.issue} onClick={() => setQualityIssue(item.issue)}>
                                    <Icon name={item.icon} />
                                    <strong>{formatNumber(item.value)}</strong>
                                    <span>{item.label}</span>
                                    <Icon name="chevron right" />
                                </button>
                            ))}
                        </div>
                    </Segment>
                )}
                <Segment>
                    <Header as="h3" className="dashboard-payment-heading">
                        <Icon name="credit card outline" /> Cobranza por método de pago
                        <Popup
                            content={paymentMethodHelp}
                            hoverable
                            on={["hover", "focus"]}
                            position="bottom left"
                            wide="very"
                            trigger={(
                                <button
                                    type="button"
                                    className="dashboard-help-icon"
                                    aria-label="Ayuda sobre las categorías de método de pago"
                                >
                                    <Icon name="help circle" />
                                </button>
                            )}
                        />
                    </Header>
                    <div className="dashboard-table-scroll">
                        <Table compact basic="very" selectable className="dashboard-table dashboard-clickable-table">
                            <Table.Header><Table.Row><Table.HeaderCell>Método</Table.HeaderCell><Table.HeaderCell textAlign="right">Pedidos</Table.HeaderCell><Table.HeaderCell textAlign="right">Cobrado</Table.HeaderCell><Table.HeaderCell textAlign="right">Saldo</Table.HeaderCell><Table.HeaderCell /></Table.Row></Table.Header>
                            <Table.Body>{data.byPaymentMethod.map((item) => (
                                <Table.Row
                                    key={item.code}
                                    className="dashboard-clickable-row"
                                    tabIndex={0}
                                    onClick={() => setOrdersDrilldown({
                                        key: `payment-${item.code}`,
                                        title: `Cobranza · ${item.label}`,
                                        description: paymentMethodDescription(item.code),
                                        icon: "credit card outline",
                                        filters: { ...filters, attentionOnly: false, paymentMethod: item.code },
                                        summary: [
                                            { label: "Pedidos", value: formatNumber(item.orderCount) },
                                            { label: "Valor", value: formatCurrency(item.totalAmount) },
                                            { label: "Cobrado", value: formatCurrency(item.collectedAmount) },
                                            { label: "Saldo", value: formatCurrency(item.outstandingBalance) },
                                        ],
                                    })}
                                    onKeyDown={(event: React.KeyboardEvent) => activateRow(event, () => setOrdersDrilldown({
                                        key: `payment-${item.code}`,
                                        title: `Cobranza · ${item.label}`,
                                        description: paymentMethodDescription(item.code),
                                        icon: "credit card outline",
                                        filters: { ...filters, attentionOnly: false, paymentMethod: item.code },
                                        summary: [
                                            { label: "Pedidos", value: formatNumber(item.orderCount) },
                                            { label: "Valor", value: formatCurrency(item.totalAmount) },
                                            { label: "Cobrado", value: formatCurrency(item.collectedAmount) },
                                            { label: "Saldo", value: formatCurrency(item.outstandingBalance) },
                                        ],
                                    }))}
                                >
                                    <Table.Cell><strong>{item.label}</strong></Table.Cell>
                                    <Table.Cell textAlign="right">{formatNumber(item.orderCount)}</Table.Cell>
                                    <Table.Cell textAlign="right">{formatCurrency(item.collectedAmount)}</Table.Cell>
                                    <Table.Cell textAlign="right">{formatCurrency(item.outstandingBalance)}</Table.Cell>
                                    <Table.Cell collapsing><Icon name="chevron right" color="grey" /></Table.Cell>
                                </Table.Row>
                            ))}</Table.Body>
                        </Table>
                    </div>
                </Segment>
            </div>

            <DataQualityModal
                open={Boolean(qualityIssue)}
                issue={qualityIssue}
                filters={filters}
                onClose={() => setQualityIssue(undefined)}
            />
            <ProductOrdersModal
                key={selectedProduct?.productId || "closed-product-drilldown"}
                product={selectedProduct}
                filters={filters}
                onClose={() => setSelectedProduct(undefined)}
            />
            <DashboardOrdersModal
                key={ordersDrilldown?.key || "closed-row-drilldown"}
                drilldown={ordersDrilldown}
                onClose={() => setOrdersDrilldown(undefined)}
            />
        </div>
    );
};

export default ExecutiveDashboard;
